import { Maybe } from '@varo/graphql-schema-types';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import PressableWithDelay from 'shared/components/PressableWithDelay/PressableWithDelay';
import IconAvatar, {
  IconAvatarProps,
} from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import { Tag, TagProps, TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import { isWeb } from 'shared/constants/platform';
import { PressableAnalyticsProps } from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';

import TransactionListItemBanner, {
  TransactionListItemBannerProps,
} from './TransactionListItemBanner/TransactionListItemBanner';

export enum amountStyles {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  NEUTRAL = 'NEUTRAL',
}

export interface TransactionListItemProps extends PressableAnalyticsProps {
  isPending: boolean;
  titleText: string;
  // you can pass 'June 10 • 2024' or you can pass 'foo bar biz bah' - the component shouldn't need to format anything
  secondaryText?: string;
  tags?: TagProps[];
  amountText?: string;
  // amount style changes color of text to predefined values
  amountStyle: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
  balanceText?: string;
  iconAvatar?: IconAvatarProps;
  onPress?: Maybe<() => void>;
  banner?: TransactionListItemBannerProps;
  isDisabled?: boolean;
  accessibilityLabel?: string;
  /*
   * optionally displays the chevron.
   */
  showsChevron?: Maybe<boolean>;
}

const TransactionListItem = ({
  isPending,
  titleText,
  secondaryText,
  isDisabled,
  accessibilityLabel,
  iconAvatar,
  amountText,
  amountStyle,
  balanceText,
  tags,
  banner,
  onPress,
  analyticsId = '',
  customAttributes = null,
  showsChevron = false,
}: TransactionListItemProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [showBanner, setShowBanner] = useState(!!banner?.body);

  const handleCloseBanner = () => {
    setShowBanner(false);
    banner?.onClose?.();
  };

  const chevronColor = isDisabled
    ? theme.color.interactive.disabled.text
    : theme.color.text.primary;

  const listContent = (
    <>
      <View style={styles.listItem}>
        <View style={styles.iconTextWrapper}>
          {iconAvatar ? (
            <IconAvatar {...iconAvatar} iconAccessibilityLabel="account-icon" />
          ) : null}
          <View style={styles.column}>
            <BodyBaseBoldText
              style={[
                isDisabled && interactiveStyles.textDisable,
                isPending && interactiveStyles.pendingState,
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
              testID="transaction-list-item-title"
            >
              {titleText}
            </BodyBaseBoldText>
            {secondaryText ? (
              <BodySmText
                style={[
                  isDisabled && interactiveStyles.textDisable,
                  interactiveStyles.pendingState,
                ]}
                ellipsizeMode="tail"
                numberOfLines={1}
                testID="transaction-list-item-secondaryText"
              >
                {secondaryText}
              </BodySmText>
            ) : null}
          </View>
        </View>
        <View style={styles.balance}>
          <BodyBaseBoldText
            style={[
              amountStyle === amountStyles.POSITIVE
                ? interactiveStyles.textPositive
                : interactiveStyles.textBalance,
              isDisabled && interactiveStyles.textDisable,
              isPending && interactiveStyles.pendingState,
            ]}
            testID="transaction-list-item-amount"
            numberOfLines={0}
          >
            {amountText}
          </BodyBaseBoldText>
          {!isPending && balanceText ? (
            <BodySmText
              style={[
                isDisabled && interactiveStyles.textDisable,
                interactiveStyles.pendingState,
              ]}
              testID="transaction-list-item-balance"
              numberOfLines={0}
            >
              {balanceText}
            </BodySmText>
          ) : null}
        </View>
        {showsChevron ? (
          <View style={styles.chevronContainer} testID="list-item-chevron">
            <Icon iconKey="CHEVRON_RIGHT" iconColor={chevronColor} />
          </View>
        ) : null}
      </View>
      {tags && tags.length > 0 ? (
        <View style={styles.tagWrapper}>
          {tags.map((item, index) => {
            if (index < 3) {
              return (
                <Tag
                  key={`Tag-${item.type}-${item.text}`}
                  text={item?.text}
                  type={item.type}
                />
              );
            }
          })}
          {tags.length > 3 ? (
            <Tag text={`+${tags.length - 3}`} type={TagType.INFO} />
          ) : null}
        </View>
      ) : null}

      {showBanner ? (
        <TransactionListItemBanner {...banner} onClose={handleCloseBanner} />
      ) : null}
    </>
  );

  if (onPress) {
    return (
      <PressableWithDelay
        analyticsId={analyticsId}
        customAttributes={customAttributes}
        disabled={Boolean(isDisabled)}
        aria-disabled={isDisabled}
        accessible
        accessibilityLabel={accessibilityLabel}
        role="button"
        onPress={() => {
          onPress();
          if (isWeb) {
            setIsFocused(false);
          }
        }}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        onPressIn={() => setIsPressed(true)}
        onPressOut={() => setIsPressed(false)}
        style={[
          styles.interactiveContainer,
          isFocused && interactiveStyles.focus,
          isHovered && interactiveStyles.hover,
          isPressed && interactiveStyles.press,
          isDisabled && interactiveStyles.disable,
        ]}
        testID="transaction-list-item"
      >
        {listContent}
      </PressableWithDelay>
    );
  }
  return (
    <View
      style={[
        styles.interactiveContainer,
        isDisabled && interactiveStyles.disable,
      ]}
      testID="transaction-list-item"
      accessible
      accessibilityLabel={accessibilityLabel}
      aria-disabled={isDisabled}
    >
      {listContent}
    </View>
  );
};

const styles = StyleSheet.create({
  balance: {
    alignItems: 'flex-end',
  },
  chevronContainer: {
    height: 24,
    width: 24,
  },
  column: {
    flexDirection: 'column',
    flex: 1,
  },
  iconTextWrapper: {
    flexDirection: 'row',
    flex: 1,
    gap: theme.spacing.s,
  },
  interactiveContainer: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: 'transparent',
    borderStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: theme.spacing.m,
    width: '100%',
  },
  listItem: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.s,
    width: '100%',
  },
  tagWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
});

const interactiveStyles = StyleSheet.create({
  disable: {
    backgroundColor: theme.color.interactive.secondary,
  },
  focus: {
    backgroundColor: theme.color.interactive.secondary,
    borderColor: theme.color.interactive.focused.border,
    borderWidth: 1,
  },
  hover: {
    backgroundColor: theme.color.interactive.secondaryHovered,
    borderColor: theme.color.interactive.secondaryHovered,
  },
  pendingState: {
    color: theme.color.text.secondary,
  },
  press: {
    backgroundColor: theme.color.interactive.secondaryPressed,
    borderColor: theme.color.interactive.secondaryPressed,
    borderWidth: 1,
  },
  textBalance: { color: theme.color.text.primary },
  textDisable: {
    color: theme.color.interactive.disabled.text,
  },
  textPositive: { color: theme.color.status.positive },
});

export default TransactionListItem;
